// attendanceApi.js - UPDATED for simplified backend structure
import axiosInstance from './axiosInstance';

const attendanceApi = {
  // ==================== TODAY'S SUMMARY ====================
  /**
   * Get today's attendance summary (Manager/HR only)
   * Shows: total_employees, who's present/absent today
   * @returns {Promise} Response: { success, summary, records }
   */
  getTodaySummary: () => {
    return axiosInstance.get('/api/attendance/today/');
  },

  // ==================== MY ATTENDANCE (Current User) ====================
  /**
   * Get current user's attendance with summary and history
   * @param {Object} params - Query parameters
   * @param {number} params.month - Month (1-12) (optional)
   * @param {number} params.year - Year (optional)
   * @param {string} params.from_date - Start date YYYY-MM-DD (optional)
   * @param {string} params.to_date - End date YYYY-MM-DD (optional)
   * @param {string} params.status - Filter by status (optional)
   * @returns {Promise} Response: { success, employee, filters, summary, records, count }
   */
  getMyAttendance: (params = {}) => {
    return axiosInstance.get('/api/attendance/my-attendance/', { params });
  },

  // ==================== EMPLOYEE SUMMARY (Manager/HR) ====================
  /**
   * Get specific employee's attendance summary and history
   * REQUIRES employee_id parameter - Manager/HR only
   * @param {number} employeeId - Employee ID (REQUIRED)
   * @param {Object} params - Query parameters
   * @param {number} params.month - Month (1-12) (optional)
   * @param {number} params.year - Year (optional)
   * @param {string} params.from_date - Start date (optional)
   * @param {string} params.to_date - End date (optional)
   * @param {string} params.status - Filter by status (optional)
   * @returns {Promise} Response: { success, employee, filters, summary, records, count, total_employees }
   */
  getEmployeeSummary: (employeeId, params = {}) => {
    return axiosInstance.get('/api/attendance/summary/', { 
      params: {
        employee_id: employeeId,
        ...params
      }
    });
  },

  // ==================== GET ALL EMPLOYEES ====================
  /**
   * Get list of all employees for dropdown
   * @returns {Promise} Response with employee list
   */
  getAllEmployees: () => {
    return axiosInstance.get('/api/employees/');
  },

  // ==================== LIVE ATTENDANCE ====================
  /**
   * Get employees currently checked in (Manager/HR only)
   * @returns {Promise} Response: { date, count, records }
   */
  getLiveAttendance: () => {
    return axiosInstance.get('/api/attendance/live/');
  },

  // ==================== LEGACY ENDPOINTS - For backward compatibility ====================
  /**
   * @deprecated Use getEmployeeSummary instead
   */
  getEmployeeAttendance: (employeeId, params = {}) => {
    return attendanceApi.getEmployeeSummary(employeeId, params);
  },

  /**
   * @deprecated Use getTodaySummary instead for today's view
   * or getEmployeeSummary for specific employee
   */
  getAttendanceSummary: (params = {}) => {
    console.warn('getAttendanceSummary is deprecated. Use getTodaySummary or getEmployeeSummary.');
    if (params.employee_id) {
      return attendanceApi.getEmployeeSummary(params.employee_id, params);
    }
    return attendanceApi.getTodaySummary();
  },

  /**
   * @deprecated Use getTodaySummary instead
   */
  getAttendanceRecords: (params = {}) => {
    return attendanceApi.getTodaySummary();
  },

  // ==================== UPDATE ATTENDANCE ====================
  /**
   * Update attendance status
   * PATCH /api/attendance/{id}/update-status/
   * Permissions: Admin, HR, Department Head (own dept)
   * @param {number} attendanceId - Attendance record ID
   * @param {Object} data - Update data
   * @param {string} data.status - New status: PRESENT | ABSENT | LATE | HALF_DAY | LEAVE | MEDICAL_LEAVE | WEEK_OFF
   * @param {string} [data.remarks] - Optional reason for the change
   * @returns {Promise} Response: { success, message, attendance, updated_by }
   */
  updateAttendanceStatus: (attendanceId, data) => {
    return axiosInstance.patch(`/api/attendance/${attendanceId}/update-status/`, data);
  },

  /**
   * Update punch in / punch out times
   * PATCH /api/attendance/{id}/update-punches/
   * Permissions: Admin or HR only
   * Status is automatically recalculated by the backend from the new times.
   * @param {number} attendanceId - Attendance record ID
   * @param {Object} data - Update data (at least one of punch_in / punch_out required)
   * @param {string} [data.punch_in]  - ISO-8601 local datetime e.g. "2025-02-20T09:05:00"
   * @param {string} [data.punch_out] - ISO-8601 local datetime e.g. "2025-02-20T18:10:00"
   * @param {string} [data.remarks]   - Optional reason e.g. "Biometric reader error"
   * @returns {Promise} Response: { success, message, attendance, updated_by }
   */
  updatePunches: (attendanceId, data) => {
    return axiosInstance.patch(`/api/attendance/${attendanceId}/update-punches/`, data);
  },

  /**
   * Update full attendance record
   * @param {number} attendanceId - Attendance record ID
   * @param {Object} data - Full update data
   * @returns {Promise} Axios promise with updated attendance
   */
  updateAttendance: (attendanceId, data) => {
    return axiosInstance.put(`/api/attendance/${attendanceId}/`, data);
  },

  // ==================== CREATE ATTENDANCE ====================
  /**
   * Create new attendance record (Manual entry)
   * @param {Object} data - Attendance data
   * @returns {Promise} Axios promise with created attendance
   */
  createAttendance: (data) => {
    return axiosInstance.post('/api/attendance/', data);
  },

  // ==================== DELETE ATTENDANCE ====================
  /**
   * Delete attendance record
   * @param {number} attendanceId - Attendance record ID
   * @returns {Promise} Axios promise
   */
  deleteAttendance: (attendanceId) => {
    return axiosInstance.delete(`/api/attendance/${attendanceId}/`);
  },

  // ==================== BULK OPERATIONS ====================
  /**
   * Bulk update attendance records
   * @param {Array} records - Array of {id, status} objects
   * @returns {Promise} Axios promise
   */
  bulkUpdateAttendance: (records) => {
    return axiosInstance.post('/api/attendance/bulk-update/', { records });
  },

  // ==================== EXPORT ====================
  /**
   * Export attendance data to CSV
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios promise with CSV file
   */
  exportAttendanceCSV: (params = {}) => {
    return axiosInstance.get('/api/attendance/export/', {
      params,
      responseType: 'blob',
    });
  },
};

export default attendanceApi;
export { attendanceApi };